import React, { useState, useEffect } from 'react';

import { Star, Filter, ChevronDown } from 'lucide-react';
import { reviewService } from '../../services/reviewService';
import Header from '../../components/ui/Header';
import ReviewCard from './components/ReviewCard';
import FilterSidebar from './components/FilterSidebar';

const ReviewsListing = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    rating: null,
    isVerified: null,
    productId: null
  });
  const [stats, setStats] = useState(null);
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const ITEMS_PER_PAGE = 12;

  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, [filters, page]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const data = await reviewService?.getAllReviews(
        filters,
        ITEMS_PER_PAGE,
        page * ITEMS_PER_PAGE
      );
      
      if (page === 0) {
        setReviews(data);
      } else {
        setReviews(prev => [...prev, ...data]);
      }
      
      setHasMore(data?.length === ITEMS_PER_PAGE);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const statsData = await reviewService?.getReviewsStats(filters?.productId);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0);
    setReviews([]);
  };

  const loadMore = () => {
    setPage(prev => prev + 1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Customer Reviews
          </h1>
          <p className="text-lg text-gray-600">
            Read authentic reviews from verified customers
          </p>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">
                  {stats?.averageRating}
                </div>
                <div className="flex items-center justify-center mb-2">
                  {[1, 2, 3, 4, 5]?.map((star) => (
                    <Star
                      key={star}
                      className={`w-5 h-5 ${
                        star <= Math.round(stats?.averageRating)
                          ? 'fill-yellow-400 text-yellow-400' :'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <div className="text-sm text-gray-600">
                  {stats?.totalReviews} Total Reviews
                </div>
              </div>

              <div className="md:col-span-2">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Rating Distribution
                </h3>
                {[5, 4, 3, 2, 1]?.map((rating) => {
                  const count = stats?.ratingDistribution?.[rating] || 0;
                  const percentage = stats?.totalReviews > 0
                    ? (count / stats?.totalReviews) * 100
                    : 0;

                  return (
                    <div key={rating} className="flex items-center mb-2">
                      <span className="text-sm text-gray-600 w-12">
                        {rating} <Star className="inline w-3 h-3 fill-yellow-400 text-yellow-400" />
                      </span>
                      <div className="flex-1 mx-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-600 transition-all duration-300"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600 w-12 text-right">
                        {count}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-8">
          {/* Filter Sidebar - Desktop */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <FilterSidebar
              filters={filters}
              onFilterChange={handleFilterChange}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Mobile Filter Button */}
            <div className="lg:hidden mb-6">
              <button
                onClick={() => setShowFilterSidebar(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <Filter className="w-5 h-5" />
                <span className="font-medium">Filters</span>
              </button>
            </div>

            {/* Reviews Grid */}
            {loading && page === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
              </div>
            ) : reviews?.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                <p className="text-gray-500 text-lg">No reviews found</p>
                <p className="text-gray-400 mt-2">Try adjusting your filters</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {reviews?.map((review) => (
                    <ReviewCard key={review?.id} review={review} />
                  ))}
                </div>

                {/* Load More Button */}
                {hasMore && (
                  <div className="flex justify-center">
                    <button
                      onClick={loadMore}
                      disabled={loading}
                      className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                          <span>Loading...</span>
                        </>
                      ) : (
                        <>
                          <span>Load More Reviews</span>
                          <ChevronDown className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {/* Mobile Filter Sidebar */}
      {showFilterSidebar && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowFilterSidebar(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white shadow-xl overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Filters</h2>
                <button
                  onClick={() => setShowFilterSidebar(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <FilterSidebar
                filters={filters}
                onFilterChange={(newFilters) => {
                  handleFilterChange(newFilters);
                  setShowFilterSidebar(false);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsListing;